import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { Search, Filter, Layers, Palette, RefreshCw, ChevronDown, ChevronRight, ArrowUp, ArrowDown, MoreVertical } from 'lucide-react';
import { SupplyRecord, SupplyStatus, SUPPLY_STATUS_CONFIG, ColumnFilter, TableSort, TableGrouping } from '../types/supply';
import { exportRowsToExcel } from '../utils/excelExport';

const normalizeSearchText = (value: unknown) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

interface Column {
  key: keyof SupplyRecord;
  label: string;
  width?: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, record: SupplyRecord) => React.ReactNode;
}

interface AdvancedTableProps {
  data: SupplyRecord[];
  columns: Column[];
  onRefresh?: () => void;
  selectedRows?: string[];
  onSelectedRowsChange?: (rows: string[]) => void;
  fixedFirstColumn?: boolean;
  onRowDoubleClick?: (record: SupplyRecord) => void;
  disablePagination?: boolean;
  fixedTableHeight?: number;
  minSearchLength?: number;
  getRowClassName?: (record: SupplyRecord) => string;
}

export function AdvancedTable({ data, columns, onRefresh, selectedRows: controlledSelectedRows, onSelectedRowsChange, fixedFirstColumn = false, onRowDoubleClick, disablePagination = false, fixedTableHeight, minSearchLength = 0, getRowClassName }: AdvancedTableProps) {
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [sort, setSort] = useState<TableSort | null>(null);
  const [groupBy, setGroupBy] = useState<TableGrouping | null>(null);
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [showMobileTools, setShowMobileTools] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const selectedRows = controlledSelectedRows ? new Set(controlledSelectedRows) : internalSelectedRows;

  // Filtreleme
  const filteredData = useMemo(() => {
    let result = [...data];

    // Global arama
    if (globalSearch && globalSearch.length >= minSearchLength) {
      const normalizedGlobalSearch = normalizeSearchText(globalSearch);
      result = result.filter(record => 
        Object.values(record).some(value => 
          normalizeSearchText(value).includes(normalizedGlobalSearch)
        )
      );
    } else if (globalSearch && globalSearch.length < minSearchLength) {
      result = [];
    }

    // Sütun filtreleri
    columnFilters.forEach(filter => {
      result = result.filter(record => {
        const value = String(record[filter.field as keyof SupplyRecord]).toLowerCase();
        const filterValue = filter.value.toLowerCase();
        
        switch (filter.operator) {
          case 'contains':
            return value.includes(filterValue);
          case 'equals':
            return value === filterValue;
          case 'startsWith':
            return value.startsWith(filterValue);
          case 'endsWith':
            return value.endsWith(filterValue);
          default:
            return true;
        }
      });
    });

    return result;
  }, [data, globalSearch, columnFilters]);

  // Sıralama
  const sortedData = useMemo(() => {
    if (!sort) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sort.field as keyof SupplyRecord];
      const bVal = b[sort.field as keyof SupplyRecord];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sort]);

  // Gruplama
  const groupedData = useMemo(() => {
    if (!groupBy) return { ungrouped: sortedData };

    const groups: Record<string, SupplyRecord[]> = {};
    sortedData.forEach(record => {
      const key = String(record[groupBy.field as keyof SupplyRecord]);
      if (!groups[key]) groups[key] = [];
      groups[key].push(record);
    });

    return groups;
  }, [sortedData, groupBy]);

  const totalRows = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const activeFilterCount = columnFilters.filter((item) => item.value).length + (globalSearch ? 1 : 0);

  useEffect(() => {
    setPage(1);
  }, [globalSearch, columnFilters, groupBy, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedData = useMemo(() => {
    if (groupBy || disablePagination) return sortedData;
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, groupBy, page, pageSize, disablePagination]);

  const handleSort = (field: keyof SupplyRecord) => {
    if (!sort || sort.field !== field) {
      setSort({ field: field as string, direction: 'asc' });
    } else if (sort.direction === 'asc') {
      setSort({ field: field as string, direction: 'desc' });
    } else {
      setSort(null);
    }
  };

  const handleColumnFilter = (field: string, value: string) => {
    if (!value) {
      setColumnFilters(prev => prev.filter(f => f.field !== field));
    } else {
      setColumnFilters(prev => {
        const existing = prev.findIndex(f => f.field === field);
        if (existing >= 0) {
          const newFilters = [...prev];
          newFilters[existing] = { field, value, operator: 'contains' };
          return newFilters;
        }
        return [...prev, { field, value, operator: 'contains' }];
      });
    }
  };

  const toggleGroupExpand = (groupKey: string) => {
    if (!groupBy) return;
    setGroupBy({
      ...groupBy,
      expanded: {
        ...groupBy.expanded,
        [groupKey]: !groupBy.expanded[groupKey],
      },
    });
  };

  const toggleRowSelection = (id: string) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    if (onSelectedRowsChange) {
      onSelectedRowsChange(Array.from(newSelection));
    } else {
      setInternalSelectedRows(newSelection);
    }
  };

  const StatusBadge = ({ status }: { status: SupplyStatus }) => {
    const config = SUPPLY_STATUS_CONFIG[status];
    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
        style={{ backgroundColor: config.bgColor, color: config.color }}
      >
        {config.label}
      </span>
    );
  };

  const renderCellValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return value.length > 0 ? `${value.length}` : '-';
    if (typeof value === 'object') return '-';
    return value as React.ReactNode;
  };

  const handleExportExcel = () => {
    exportRowsToExcel(filteredData, 'datatable-export.xlsx', {
      sheetName: 'Data',
      columns: columns.map((column) => ({
        key: String(column.key),
        label: column.label,
        value: (row) => row[column.key],
      })),
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <div className="workiom-toolbar !justify-start !px-4 !py-2">
        <button
          type="button"
          onClick={() => setShowMobileTools((current) => !current)}
          className={`workiom-outline-button !text-xs !py-1.5 md:!hidden ${showMobileTools ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}
        >
          <Search className="w-4 h-4" />
          Ara / Filtre
          {activeFilterCount > 0 && <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700">{activeFilterCount}</span>}
        </button>

        {/* Global Search */}
        <div className={`${showMobileTools ? 'flex' : 'hidden'} workiom-search min-w-0 flex-1 max-w-[360px] md:flex`}>
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={minSearchLength > 0 ? `Ara (en az ${minSearchLength} harf)` : 'Bul'}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            className="w-full !text-xs !pl-8 !py-1.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className={`${showMobileTools ? 'flex' : 'hidden'} w-full flex-wrap items-center gap-2 md:flex md:w-auto`}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`workiom-outline-button !text-xs !py-1.5 transition-colors ${
              showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtrele
          </button>

          <button
            onClick={() => setShowGrouping(!showGrouping)}
            className={`workiom-outline-button !text-xs !py-1.5 transition-colors ${
              showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''
            }`}
          >
            <Layers className="w-4 h-4" />
            Gruplandır
            {groupBy && <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">1</span>}
          </button>

          <button className="workiom-outline-button !text-xs !py-1.5">
            <Palette className="w-4 h-4" />
            Renk
          </button>

          <button
            onClick={onRefresh}
            className="workiom-soft-button !text-xs !py-1.5"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={handleExportExcel}
            className="workiom-outline-button !text-xs !py-1.5"
          >
            Excele Aktar
          </button>

          <button className="workiom-outline-button !px-2.5 !py-1.5 !text-xs">
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>

        <div className="ml-auto text-xs text-gray-600">
          {selectedRows.size > 0 && `${selectedRows.size} seçili • `}
          {filteredData.length} kayıt
        </div>
      </div>

      {/* Grouping Panel */}
      {showGrouping && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-700 font-medium">Grupla:</span>
            <select
              value={groupBy?.field || ''}
              onChange={(e) => {
                if (e.target.value) {
                  setGroupBy({ field: e.target.value, expanded: {} });
                } else {
                  setGroupBy(null);
                }
              }}
              className="text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="">Gruplandırma yok</option>
              <option value="durum">Durum</option>
              <option value="yuklemeIl">Yükleme İl</option>
              <option value="teslimIl">Teslim İl</option>
              <option value="aracTipi">Araç Tipi</option>
            </select>
            {groupBy && (
              <button
                onClick={() => setGroupBy(null)}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Temizle
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="workiom-grid-wrap visible-scrollbars flex-1 w-full max-w-full overflow-x-auto overflow-y-auto" style={fixedTableHeight ? { height: `${fixedTableHeight}px`, maxHeight: `${fixedTableHeight}px` } : undefined}>
        <table className="workiom-grid-table text-xs">
          <thead>
            <tr>
              <th className="workiom-grid-index !px-2 !py-2">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={selectedRows.size === filteredData.length && filteredData.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const next = filteredData.map(r => String(r.id));
                      if (onSelectedRowsChange) {
                        onSelectedRowsChange(next);
                      } else {
                        setInternalSelectedRows(new Set(next));
                      }
                    } else {
                      if (onSelectedRowsChange) {
                        onSelectedRowsChange([]);
                      } else {
                        setInternalSelectedRows(new Set());
                      }
                    }
                  }}
                />
              </th>
              {columns.map((column, columnIndex) => (
                <th
                  key={column.key}
                  className={`!px-3 !py-2 text-left whitespace-nowrap ${fixedFirstColumn && columnIndex === 0 ? 'workiom-grid-sticky-col' : ''}`}
                  style={{ width: column.width }}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      {column.label}
                      {column.sortable && (
                        <button
                          onClick={() => handleSort(column.key)}
                          className="p-0.5 hover:bg-gray-200 rounded"
                        >
                          {sort?.field === column.key ? (
                            sort.direction === 'asc' ? (
                              <ArrowUp className="w-3 h-3 text-blue-600" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-blue-600" />
                            )
                          ) : (
                            <ArrowUp className="w-3 h-3 text-gray-400" />
                          )}
                        </button>
                      )}
                    </div>
                    {showFilters && column.filterable && (
                      <input
                        type="text"
                        placeholder="Filtrele..."
                        value={columnFilters.find(f => f.field === column.key)?.value || ''}
                        onChange={(e) => handleColumnFilter(column.key as string, e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupBy ? (
              // Grouped view
              Object.entries(groupedData).map(([groupKey, records]) => {
                const isExpanded = groupBy.expanded[groupKey] !== false;
                const statusConfig = groupKey in SUPPLY_STATUS_CONFIG 
                  ? SUPPLY_STATUS_CONFIG[groupKey as SupplyStatus]
                  : null;
                
                return (
                  <React.Fragment key={groupKey}>
                    <tr className="bg-gray-100 hover:bg-gray-150">
                       <td colSpan={columns.length + 1} className="!px-3 !py-2 border-b border-gray-200">
                        <button
                          onClick={() => toggleGroupExpand(groupKey)}
                          className="flex items-center gap-2 text-sm font-medium text-gray-900"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          {statusConfig ? (
                            <StatusBadge status={groupKey as SupplyStatus} />
                          ) : (
                            <span>{groupKey}</span>
                          )}
                          <span className="text-gray-600">({records.length})</span>
                        </button>
                      </td>
                    </tr>
                    {isExpanded && records.map((record) => (
                       <tr key={record.id} data-row-id={record.id} onDoubleClick={() => onRowDoubleClick?.(record)} className={`${onRowDoubleClick ? 'cursor-pointer' : ''} ${getRowClassName?.(record) || ''}`.trim()}>
                         <td className={`workiom-grid-index !px-2 !py-2 ${fixedFirstColumn ? '' : '!static'}`}>
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={selectedRows.has(String(record.id))}
                            onChange={() => toggleRowSelection(String(record.id))}
                          />
                        </td>
                        {columns.map((column, columnIndex) => (
                           <td key={column.key} className={`${fixedFirstColumn && columnIndex === 0 ? 'workiom-grid-sticky-col' : ''}`}>
                            {column.render 
                              ? column.render(record[column.key], record)
                              : renderCellValue(record[column.key])
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              // Ungrouped view
              paginatedData.map((record) => (
                <tr key={record.id} data-row-id={record.id} onDoubleClick={() => onRowDoubleClick?.(record)} className={`${onRowDoubleClick ? 'cursor-pointer' : ''} ${getRowClassName?.(record) || ''}`.trim()}>
                  <td className={`workiom-grid-index !px-2 !py-2 ${fixedFirstColumn ? '' : '!static'}`}>
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={selectedRows.has(String(record.id))}
                      onChange={() => toggleRowSelection(String(record.id))}
                    />
                  </td>
                  {columns.map((column, columnIndex) => (
                    <td key={column.key} className={`${fixedFirstColumn && columnIndex === 0 ? 'workiom-grid-sticky-col' : ''}`}>
                      {column.render 
                        ? column.render(record[column.key], record)
                        : renderCellValue(record[column.key])
                      }
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!groupBy && totalRows > 0 && !disablePagination && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalRows)} / {totalRows}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            <button
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
            >
              Geri
            </button>
            <span className="text-xs text-gray-700">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
            >
              İleri
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
